import { skipToken } from "@reduxjs/toolkit/query";
import { AiCreditBalanceLevel } from "@repo/app-types/entities/creditUsage";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { IconAlertTriangle, IconX } from "@tabler/icons-react";
import { useState, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useAiCreditStatusQuery } from "../../api/entities/company.js";
import { useTeammateAuthorization } from "../../helpers/teammateAuthorizationContext.js";

/** Where a company adds AI credits: they are sold through sales, never self-serve. */
export const AI_CREDITS_SALES_HREF = "mailto:sales@alpharun.com";

/** How often a mounted warning re-reads the AI credit status. */
export const AI_CREDIT_STATUS_POLLING_MS = 10 * 60_000;

const DISMISSED_LEVEL_STORAGE_KEY = "admin.aiCreditsLimitBanner.dismissedLevel";

export type AiCreditsWarningLevel = Exclude<
  AiCreditBalanceLevel,
  typeof AiCreditBalanceLevel.Available
>;

interface AiCreditsLimitBannerViewProps {
  level: AiCreditsWarningLevel;
  onDismiss: () => void;
}

/** The app-wide notice that the company's AI credits are low or used up. */
export const AiCreditsLimitBannerView: FC<AiCreditsLimitBannerViewProps> = (
  props,
) => {
  const { level, onDismiss } = props;
  const { iconClassName, message } = describeLevel(level);

  return (
    <div
      className="relative flex min-h-9 w-full items-center justify-center bg-gray-700 px-12 py-1.5 text-center text-sm leading-5 text-white"
      role="status"
    >
      <div className="flex min-w-0 items-center justify-center gap-2">
        <IconAlertTriangle
          aria-hidden="true"
          className={twMerge("size-5 shrink-0", iconClassName)}
        />
        <p>{message}</p>
      </div>
      <IconButton
        accessibleLabel="Dismiss AI credit notice"
        className="absolute top-1/2 right-2 -translate-y-1/2 text-white/70 hover:bg-white/10 hover:text-white"
        icon={<IconX aria-hidden="true" className="size-4" />}
        onClick={onDismiss}
        size={Size.SMALL}
        variant={ButtonVariant.Tertiary}
      />
    </div>
  );
};

function describeLevel(level: AiCreditsWarningLevel): {
  iconClassName: string;
  message: ReactNode;
} {
  const contactSales = (
    <a
      className="font-semibold underline underline-offset-2 hover:no-underline"
      href={AI_CREDITS_SALES_HREF}
    >
      Contact sales
    </a>
  );
  switch (level) {
    case AiCreditBalanceLevel.RunningLow:
      return {
        iconClassName: "text-amber-400",
        message: (
          <>
            AI credits are running low. {contactSales} to add credits and keep
            Ask Alpharun, Signals, Automations, and AI MCP/API tools running.
          </>
        ),
      };
    case AiCreditBalanceLevel.Exhausted:
      return {
        iconClassName: "text-red-400",
        message: (
          <>
            AI credits are used up. Ask Alpharun, Signals, Automations, and AI
            MCP/API tools may be paused. {contactSales} to add credits.
          </>
        ),
      };
  }
}

/**
 * The limit banner for billing editors. Dismissing it hides that level for the
 * browser session; reaching the next level shows it again.
 */
export const AiCreditsLimitBanner: FC = () => {
  const authorization = useTeammateAuthorization();
  const canManageBilling = authorization.can(
    PermissionActions.UPDATE,
    PermissionSubjects.BILLING,
  );
  const { data: status } = useAiCreditStatusQuery(
    canManageBilling ? undefined : skipToken,
    { pollingInterval: AI_CREDIT_STATUS_POLLING_MS },
  );
  const [dismissedLevel, setDismissedLevel] = useState(readDismissedLevel);

  if (!status) {
    return null;
  }
  const { level } = status;
  if (level === AiCreditBalanceLevel.Available || level === dismissedLevel) {
    return null;
  }

  return (
    <AiCreditsLimitBannerView
      level={level}
      onDismiss={() => {
        storeDismissedLevel(level);
        setDismissedLevel(level);
      }}
    />
  );
};

function readDismissedLevel(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISSED_LEVEL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeDismissedLevel(level: AiCreditsWarningLevel): void {
  try {
    window.sessionStorage.setItem(DISMISSED_LEVEL_STORAGE_KEY, level);
  } catch {
    // Storage can be unavailable (e.g. private browsing); state still hides it.
  }
}
